import { computed, ref } from 'vue'

import { UPDATE_PROPERTY_SUCCESS_MESSAGE } from '@/modules/Classes/propertyMessages'
import { useToast } from '@/uiKit/Toast/useToast'
import { useTeamStore } from '@/pinia/useTeamStore'
import type { TeamProperty } from '@/store/types/PropertyTypes'
import type {
  DatasetItemPropertiesResponse,
  DatasetItemPropertyActor,
  DatasetItemPropertyId,
  DatasetItemPropertyValues,
} from '@/backend/darwin/teamProperties'
import { getTeamProperties, reorderTeamProperties } from '@/backend/darwin/teamProperties'
import { defineStore } from 'pinia'
import { useUserStore } from '@/modules/Auth/useUserStore'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'

export const CREATE_PROPERTY_VALUE_KEY = 'create_value'

export const isTemporaryPropertyValueId = (id: string): boolean =>
  id.startsWith(CREATE_PROPERTY_VALUE_KEY)

/**
 * Store used to fetch all team properties and keep the loading state in sync.
 * This is also responsible for holding current item selected property values
 */
export const useTeamProperties = defineStore('properties', () => {
  const teamStore = useTeamStore()
  const membershipStore = useMembershipStore()
  const userStore = useUserStore()
  const toast = useToast()

  const properties = ref<TeamProperty[]>([])
  const loaded = ref(false)

  const snapshotDefinitions = ref<TeamProperty[]>([])

  /**
   * What the workview renders: the live properties overlaid with the definitions of the time
   * travel snapshot being shown, deleted properties and values included. Never edit from this.
   */
  const shownProperties = computed<TeamProperty[]>(() => {
    if (!snapshotDefinitions.value.length) {
      return properties.value
    }
    const byId = new Map(properties.value.map((property) => [property.id, property]))
    // a time travel snapshot's definitions win over live ones (see useStageView)
    snapshotDefinitions.value.forEach((property) => byId.set(property.id, property))
    return [...byId.values()]
  })

  const setProperties = (payload: TeamProperty[]): void => {
    properties.value = Object.seal(payload)
  }

  const fetchProperties = async (teamSlug: string): Promise<void> => {
    loaded.value = false
    const response = await getTeamProperties(teamSlug)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })
      loaded.value = true
    } else {
      setProperties(response.data.properties)
      loaded.value = true
    }
  }

  const reorderProperties = async (
    teamSlug: string,
    classId: string | number,
    propertyIds: string[],
  ): Promise<void> => {
    const result = await reorderTeamProperties(teamSlug, classId, propertyIds)
    if ('error' in result) {
      const message =
        typeof result.error.message === 'string'
          ? result.error.message
          : 'Failed to reorder properties'
      toast.error({ meta: { title: message } })
      return
    }

    // The reorder payload is scoped to a single sibling group (e.g. the
    // children of one parent property), so only the properties in that
    // group receive new positions — every other property in the class
    // (other roots, other parents' children, etc.) keeps its existing
    // position. Blindly rewriting every class-scoped property's position
    // would clobber siblings outside the reorder scope to `-1` (from
    // `indexOf` miss) and collapse them into a tied bucket until a page
    // refresh.
    //
    // Properties are sealed and non reactive - let's swap the array with a
    // new reference so reactivity fires.
    const reorderedIndex = new Map(propertyIds.map((id, index) => [id, index]))
    const updatedProperties = properties.value.map((property) => {
      const newPosition = reorderedIndex.get(property.id)
      if (newPosition === undefined) return property
      return { ...property, position: newPosition }
    })
    properties.value = Object.seal(updatedProperties)
    toast.success({ meta: { title: UPDATE_PROPERTY_SUCCESS_MESSAGE } })
  }

  const getCurrentActor = (): DatasetItemPropertyActor | null => {
    const currentTeamId = teamStore.currentTeam?.id
    if (!currentTeamId || !userStore.currentUserId) {
      return null
    }
    const member = membershipStore.getMembershipByTeamIdUserId(
      currentTeamId,
      userStore.currentUserId,
    )
    if (member) {
      return {
        user_id: member.user_id,
        role: member.role,
        model_id: undefined,
      }
    }
    return null
  }

  const currentItemProperties = ref<DatasetItemPropertiesResponse['properties']>([])
  const setCurrentItemPropertyValues = (
    propertyId: DatasetItemPropertyId,
    values: DatasetItemPropertyValues,
  ): void => {
    currentItemProperties.value = currentItemProperties.value.filter(
      (itemProperty) => itemProperty.id !== propertyId,
    )
    const member = getCurrentActor()
    currentItemProperties.value.push({
      id: propertyId,
      values,
      actors: member ? [member] : [],
    })
  }

  const setSnapshotDefinitions = (definitions: TeamProperty[]): void => {
    if (definitions.length || snapshotDefinitions.value.length) {
      snapshotDefinitions.value = definitions
    }
  }

  return {
    properties,
    reorderProperties,
    setProperties,
    shownProperties,
    fetchProperties,
    loaded,
    currentItemProperties,
    setCurrentItemPropertyValues,
    setSnapshotDefinitions,
  }
})
